import { lazy, type ComponentType } from 'react';

type PageModule<T> = { default: T };

// Long enough for a reload to land; short enough that a reload the user
// cancelled (a "Leave site?" prompt) still reaches the error boundary.
export const RELOAD_WAIT_MS = 15_000;

/**
 * When main.tsx's `vite:preloadError` handler takes over (it reloads to pick
 * up the new build), Vite resolves the failed import() with `undefined`
 * instead of rejecting, and React.lazy then throws "Cannot read properties of
 * undefined (reading 'default')". Wait for the reload instead; if it never
 * comes, reject so the error boundary can offer a manual refresh.
 */
export function loadPageModule<T>(
  factory: () => Promise<PageModule<T>>,
  waitMs: number = RELOAD_WAIT_MS,
): Promise<PageModule<T>> {
  return factory().then(mod =>
    (mod as PageModule<T> | undefined) ??
    new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('Page chunk failed to load and the reload did not happen')), waitMs)
    )
  );
}

/** React.lazy for page chunks that survives a deploy. */
export function lazyPage<T extends ComponentType<any>>(factory: () => Promise<PageModule<T>>) {
  return lazy(() => loadPageModule(factory));
}
